import React from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Result } from "../../types";

interface PDFExportProps {
  results: Result[];
  recommendations: any[];
  resultsRef: React.RefObject<HTMLDivElement>;
}

const PDFExport: React.FC<PDFExportProps> = ({
  results,
  recommendations,
  resultsRef,
}) => {
  const exportToPDF = async () => {
    const currentYear = new Date().getFullYear();
    try {
      const button = document.querySelector(
        "[data-pdf-export]"
      ) as HTMLButtonElement;
      if (button) {
        button.disabled = true;
        button.innerHTML = "⏳ Generating PDF...";
      }

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 15;
      const contentWidth = pageWidth - 2 * margin;

      // Helper function to add a new page if needed
      const checkPageBreak = (currentY: number, neededHeight: number) => {
        if (currentY + neededHeight > pageHeight - margin) {
          pdf.addPage();
          return margin;
        }
        return currentY;
      };

      // Helper function to add footer with branding to each page
      const addFooter = () => {
        // Footer background with solid color
        pdf.setFillColor(21, 42, 83); // RGB values for #152A53
        pdf.rect(0, pageHeight - 25, pageWidth, 25, "F");

        // Footer text
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(8);
        pdf.text(
          `© ${currentYear} Inthera Consulting Group. All rights reserved.`,
          pageWidth / 2,
          pageHeight - 15,
          { align: "center" }
        );

        // Add clickable link for website
        pdf.text(
          "Visit us at: intheraconsultinggroup.com",
          pageWidth / 2,
          pageHeight - 8,
          { align: "center" }
        );

        // Add clickable link
        pdf.link(pageWidth / 2 - 25, pageHeight - 12, 50, 8, {
          url: "https://intheraconsultinggroup.com/",
        });
      };

      // Load the logo image
      const loadLogoImage = (): Promise<string> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png"));
          };
          img.onerror = reject;
          img.src = "/assets/inthera-logo.png";
        });
      };

      let logoDataUrl: string | null = null;
      try {
        logoDataUrl = await loadLogoImage();
      } catch (error) {
        console.warn("Could not load logo, continuing without it:", error);
      }

      // Title Page with Enhanced Header
      pdf.setFillColor(21, 42, 83); // RGB values for #152A53
      pdf.rect(0, 0, pageWidth, 70, "F");

      // Add Inthera logo if available
      if (logoDataUrl) {
        const logoWidth = 40;
        const logoHeight = 25;
        const logoX = pageWidth / 2 - logoWidth / 2;
        const logoY = 15;

        pdf.addImage(logoDataUrl, "PNG", logoX, logoY, logoWidth, logoHeight);

        // Add clickable area for the logo
        pdf.link(logoX, logoY, logoWidth, logoHeight, {
          url: "https://intheraconsultinggroup.com/",
        });
      }

      // Main title
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.text("Digital Workplace Solutions Assessment", pageWidth / 2, 50, {
        align: "center",
      });

      pdf.setFontSize(12);
      pdf.text(
        `Generated on ${new Date().toLocaleDateString()}`,
        pageWidth / 2,
        60,
        { align: "center" }
      );

      let yPosition = 90;

      // Executive Summary
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(18);
      pdf.text("Executive Summary", margin, yPosition);
      yPosition += 18;

      pdf.setFontSize(11);
      const summaryText = `This Digital Workplace Solutions Assessment report provides personalized recommendations based on your workplace technology needs. We analyzed ${results.length} survey responses to identify ${recommendations.length} key solutions that will enhance your digital workplace and improve organizational productivity.`;
      const summaryLines = pdf.splitTextToSize(summaryText, contentWidth);
      pdf.text(summaryLines, margin, yPosition);
      yPosition += summaryLines.length * 5 + 20;

      // Key Statistics and Analytics
      yPosition = checkPageBreak(yPosition, 50);
      pdf.setFontSize(16);
      pdf.setTextColor(21, 42, 83);
      pdf.text("Key Statistics & Analytics", margin, yPosition);
      yPosition += 12;

      const highPriority = recommendations.filter(
        (r) => r.priority === "High"
      ).length;
      const mediumPriority = recommendations.filter(
        (r) => r.priority === "Medium"
      ).length;
      const lowPriority = recommendations.filter(
        (r) => r.priority === "Low"
      ).length;

      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);

      // Statistics with better formatting
      pdf.setFillColor(21, 42, 83);
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(
        `Total Recommendations: ${recommendations.length}`,
        margin + 8,
        yPosition
      );
      yPosition += 6;

      pdf.setFillColor(239, 68, 68);
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(
        `High Priority Solutions: ${highPriority}`,
        margin + 8,
        yPosition
      );
      yPosition += 6;

      pdf.setFillColor(245, 158, 11);
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(
        `Medium Priority Solutions: ${mediumPriority}`,
        margin + 8,
        yPosition
      );
      yPosition += 6;

      pdf.setFillColor(16, 185, 129);
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(`Low Priority Solutions: ${lowPriority}`, margin + 8, yPosition);
      yPosition += 15;

      // Analytics breakdown by category
      const categoryBreakdown = recommendations.reduce((acc: any, rec: any) => {
        acc[rec.category] = (acc[rec.category] || 0) + 1;
        return acc;
      }, {});

      pdf.setFontSize(12);
      pdf.setTextColor(21, 42, 83);
      pdf.text("Recommendations by Category:", margin, yPosition);
      yPosition += 8;

      pdf.setFontSize(10);
      pdf.setTextColor(75, 85, 99);
      Object.entries(categoryBreakdown).forEach(([category, count]) => {
        pdf.setFillColor(21, 42, 83);
        pdf.circle(margin + 2, yPosition - 1.5, 0.8, "F");
        pdf.text(`${category}: ${count} solutions`, margin + 8, yPosition);
        yPosition += 5;
      });
      yPosition += 10;

      // Average recommendation score
      const avgScore =
        recommendations.reduce((sum: number, rec: any) => sum + rec.score, 0) /
        recommendations.length;
      pdf.setFillColor(21, 42, 83);
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(
        `Average Recommendation Score: ${avgScore.toFixed(1)}/100`,
        margin + 8,
        yPosition
      );
      yPosition += 15;

      // Add footer to first page
      addFooter();

      // Capture Charts Section - IMPROVED APPROACH
      yPosition = checkPageBreak(yPosition, 120);
      if (yPosition === margin) {
        addFooter();
      }

      pdf.setFontSize(16);
      pdf.setTextColor(21, 42, 83);
      pdf.text("Digital Workplace Analytics Dashboard", margin, yPosition);
      yPosition += 20;

      console.log("=== Starting enhanced workplace chart capture process ===");

      let chartsAdded = 0;
      let chartY = yPosition;
      let chartX = margin;
      const chartsPerRow = 2;
      const chartWidth = (contentWidth - 20) / chartsPerRow; // Spacing between columns
      const chartHeight = 80;
      const horizontalSpacing = 20; // Consistent horizontal spacing
      const verticalSpacing = 15; // Reduced vertical spacing for better balance

      try {
        // First, try to find and click analytics button to ensure charts are visible
        console.log("Looking for analytics button...");

        const possibleButtons = [
          ...Array.from(document.querySelectorAll("button")),
          ...Array.from(document.querySelectorAll('[role="tab"]')),
          ...Array.from(document.querySelectorAll("a")),
          ...Array.from(document.querySelectorAll('[class*="tab"]')),
        ];

        const analyticsButton = possibleButtons.find((button) => {
          const text = button.textContent?.toLowerCase() || "";
          return text.includes("analytics") || text.includes("chart");
        });

        if (analyticsButton) {
          console.log("Found analytics button, clicking...");
          (analyticsButton as HTMLElement).click();

          // Wait for charts to load
          await new Promise((resolve) => setTimeout(resolve, 1500));
        } else {
          console.log(
            "Analytics button not found, looking for existing charts..."
          );
        }

        // Find specific workplace chart containers using the analytics-container class
        const chartContainers = document.querySelectorAll(
          ".analytics-container canvas, [data-chart='workplace-solutions'] canvas, [data-chart='workplace-roi'] canvas, [data-chart='workplace-timeline'] canvas, [data-chart='workplace-impact'] canvas"
        );
        console.log(
          `Found ${chartContainers.length} workplace chart containers`
        );

        // Chart titles that match our workplace results page
        const chartTitles = [
          "Solutions by Category",
          "ROI Comparison",
          "Implementation Timeline",
          "Business Impact Analysis",
        ];

        if (chartContainers.length > 0) {
          // Capture the specific workplace charts
          for (let i = 0; i < Math.min(chartContainers.length, 4); i++) {
            const container = chartContainers[i] as HTMLElement;

            try {
              console.log(
                `Capturing workplace chart ${i + 1}: ${
                  chartTitles[i] || "Chart"
                }...`
              );

              const canvas = await html2canvas(container, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: "#ffffff",
                logging: false,
                width: container.offsetWidth,
                height: container.offsetHeight,
              });

              const imgData = canvas.toDataURL("image/png");

              // Calculate position with consistent spacing
              const col = chartsAdded % chartsPerRow;
              const row = Math.floor(chartsAdded / chartsPerRow);

              const x = chartX + col * (chartWidth + horizontalSpacing);
              const y = chartY + row * (chartHeight + verticalSpacing);

              // Check if we need a new page
              if (y + chartHeight > pageHeight - margin - 35) {
                addFooter();
                pdf.addPage();
                chartY = margin + 20;
                // Recalculate position for new page
                const newRow = Math.floor(chartsAdded / chartsPerRow);
                const newY = chartY + newRow * (chartHeight + verticalSpacing);
                pdf.addImage(imgData, "PNG", x, newY, chartWidth, chartHeight);

                // Add chart title with consistent spacing
                pdf.setFontSize(10);
                pdf.setTextColor(31, 41, 55);
                pdf.text(chartTitles[i] || `Chart ${i + 1}`, x, newY - 8);
              } else {
                pdf.addImage(imgData, "PNG", x, y, chartWidth, chartHeight);

                // Add chart title with consistent spacing
                pdf.setFontSize(10);
                pdf.setTextColor(31, 41, 55);
                pdf.text(chartTitles[i] || `Chart ${i + 1}`, x, y - 8);
              }

              chartsAdded++;
              console.log(`Workplace chart ${i + 1} captured successfully`);
            } catch (error) {
              console.error(`Error capturing workplace chart ${i + 1}:`, error);
            }
          }
        } else {
          // Alternative approach: look for the analytics container and capture individual chart sections
          console.log(
            "No canvas elements found, looking for chart containers..."
          );

          const analyticsContainer = document.querySelector(
            ".analytics-container"
          );
          if (analyticsContainer) {
            const chartDivs = analyticsContainer.querySelectorAll(
              "[data-chart='workplace-solutions'], [data-chart='workplace-roi'], [data-chart='workplace-timeline'], [data-chart='workplace-impact']"
            );
            console.log(
              `Found ${chartDivs.length} workplace chart divs in analytics container`
            );

            for (let i = 0; i < Math.min(chartDivs.length, 4); i++) {
              const chartDiv = chartDivs[i] as HTMLElement;

              try {
                console.log(
                  `Capturing chart div ${i + 1}: ${chartTitles[i]}...`
                );

                const canvas = await html2canvas(chartDiv, {
                  scale: 1.5,
                  useCORS: true,
                  allowTaint: true,
                  backgroundColor: "#ffffff",
                  logging: false,
                });

                const imgData = canvas.toDataURL("image/png");

                // Calculate position with consistent spacing
                const col = chartsAdded % chartsPerRow;
                const row = Math.floor(chartsAdded / chartsPerRow);

                const x = chartX + col * (chartWidth + horizontalSpacing);
                const y = chartY + row * (chartHeight + verticalSpacing);

                // Check if we need a new page
                if (y + chartHeight > pageHeight - margin - 35) {
                  addFooter();
                  pdf.addPage();
                  chartY = margin + 20;
                  // Recalculate position for new page
                  const newRow = Math.floor(chartsAdded / chartsPerRow);
                  const newY =
                    chartY + newRow * (chartHeight + verticalSpacing);
                  pdf.addImage(
                    imgData,
                    "PNG",
                    x,
                    newY,
                    chartWidth,
                    chartHeight
                  );

                  // Add chart title with consistent spacing
                  pdf.setFontSize(10);
                  pdf.setTextColor(31, 41, 55);
                  pdf.text(chartTitles[i] || `Chart ${i + 1}`, x, newY - 8);
                } else {
                  pdf.addImage(imgData, "PNG", x, y, chartWidth, chartHeight);

                  // Add chart title with consistent spacing
                  pdf.setFontSize(10);
                  pdf.setTextColor(31, 41, 55);
                  pdf.text(chartTitles[i] || `Chart ${i + 1}`, x, y - 8);
                }

                chartsAdded++;
                console.log(`Chart div ${i + 1} captured successfully`);
              } catch (error) {
                console.error(`Error capturing chart div ${i + 1}:`, error);
              }
            }
          }
        }

        // Last resort fallback: show helpful message
        if (chartsAdded === 0) {
          console.log("No charts captured, adding help text");
          pdf.setFontSize(11);
          pdf.setTextColor(75, 85, 99);
          const helpLines = [
            "Charts are not currently visible for capture.",
            "For best results:",
            "• Navigate to the Analytics tab",
            "• Wait for charts to fully load",
            "• Then export the PDF",
          ];

          helpLines.forEach((line, index) => {
            pdf.text(line, margin, yPosition + index * 8);
          });

          yPosition += helpLines.length * 8 + 15;
        }
      } catch (error) {
        console.error("Chart capture error:", error);
      }

      // Update yPosition based on results with proper spacing
      if (chartsAdded > 0) {
        const rows = Math.ceil(chartsAdded / chartsPerRow);
        yPosition = chartY + rows * (chartHeight + verticalSpacing) + 15;
        console.log(`Chart capture complete: ${chartsAdded} charts added`);
      } else {
        yPosition += 15;
      }

      // Quiz Responses Section - START ON NEW PAGE
      addFooter();
      pdf.addPage();
      yPosition = margin;

      pdf.setFontSize(16);
      pdf.setTextColor(21, 42, 83);
      pdf.text("Your Survey Responses", margin, yPosition);
      yPosition += 15;

      results.forEach((result, index) => {
        const responseY = checkPageBreak(yPosition, 30);
        if (responseY === margin) {
          addFooter();
          yPosition = margin + 10;
        } else {
          yPosition = responseY;
        }

        // Add consistent spacing between responses
        if (index > 0) {
          yPosition += 8;
        }

        pdf.setFontSize(12);
        pdf.setTextColor(31, 41, 55);
        pdf.text(`Q${index + 1}: ${result.question}`, margin, yPosition);
        yPosition += 10;

        pdf.setFontSize(11);
        pdf.setTextColor(21, 42, 83);
        const answerLines = pdf.splitTextToSize(
          `Answer: ${result.answer}`,
          contentWidth - 10
        );
        pdf.text(answerLines, margin + 5, yPosition);
        yPosition += answerLines.length * 5 + 10;
      });

      // Add footer to current page before new page
      addFooter();

      // Detailed Recommendations Section
      pdf.addPage();
      yPosition = margin;

      pdf.setFontSize(18);
      pdf.setTextColor(21, 42, 83);
      pdf.text(
        "Digital Workplace Solutions Recommendations",
        margin,
        yPosition
      );
      yPosition += 20;

      // Sort recommendations by priority
      const sortedRecommendations = [...recommendations].sort((a, b) => {
        const priorityOrder = { High: 3, Medium: 2, Low: 1 };
        return (
          priorityOrder[b.priority as keyof typeof priorityOrder] -
          priorityOrder[a.priority as keyof typeof priorityOrder]
        );
      });

      sortedRecommendations.forEach((rec, index) => {
        const recY = checkPageBreak(yPosition, 90);
        if (recY === margin) {
          addFooter();
          yPosition = margin;
        } else {
          yPosition = recY;
        }

        // Priority badge
        const priorityColor: [number, number, number] =
          rec.priority === "High"
            ? [239, 68, 68]
            : rec.priority === "Medium"
            ? [245, 158, 11]
            : [16, 185, 129];

        pdf.setFillColor(...priorityColor);
        pdf.roundedRect(margin, yPosition - 5, 25, 8, 2, 2, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(8);
        pdf.text(rec.priority.toUpperCase(), margin + 12.5, yPosition, {
          align: "center",
        });

        // Score badge
        pdf.setFillColor(21, 42, 83);
        pdf.roundedRect(margin + 30, yPosition - 5, 20, 8, 2, 2, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.text(`${rec.score}`, margin + 40, yPosition, {
          align: "center",
        });

        // Recommendation title with numbering
        pdf.setTextColor(31, 41, 55);
        pdf.setFontSize(14);
        pdf.text(`${index + 1}. ${rec.title}`, margin + 55, yPosition);
        yPosition += 12;

        // Category and implementation info
        pdf.setFontSize(10);
        pdf.setTextColor(21, 42, 83);
        pdf.text(`Category: ${rec.category}`, margin, yPosition);
        pdf.text(`Timeline: ${rec.implementation}`, margin + 70, yPosition);
        pdf.text(`ROI: ${rec.roi}`, margin + 140, yPosition);
        yPosition += 10;

        // Description with better formatting
        pdf.setFontSize(10);
        pdf.setTextColor(75, 85, 99);
        const descLines = pdf.splitTextToSize(rec.description, contentWidth);
        pdf.text(descLines, margin, yPosition);
        yPosition += descLines.length * 4 + 8;

        // Benefits section with improved styling
        pdf.setFontSize(11);
        pdf.setTextColor(31, 41, 55);
        pdf.text("Key Benefits:", margin, yPosition);
        yPosition += 6;

        // Benefits background
        const benefitsHeight = rec.benefits.length * 5 + 4;
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(
          margin,
          yPosition - 2,
          contentWidth,
          benefitsHeight,
          2,
          2,
          "F"
        );

        rec.benefits.forEach((benefit: string) => {
          pdf.setFillColor(16, 185, 129);
          pdf.circle(margin + 5, yPosition + 1.5, 1, "F");
          pdf.setTextColor(75, 85, 99);
          pdf.setFontSize(9);
          const benefitLines = pdf.splitTextToSize(benefit, contentWidth - 15);
          pdf.text(benefitLines, margin + 10, yPosition + 3);
          yPosition += benefitLines.length * 4 + 1;
        });

        yPosition += 15; // Consistent spacing between recommendations
      });

      // Add footer to final page
      addFooter();

      pdf.save("digital-workplace-solutions-assessment.pdf");

      if (button) {
        button.disabled = false;
        button.innerHTML = "📊 Export Comprehensive Report";
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("There was an error generating the PDF. Please try again.");

      const button = document.querySelector(
        "[data-pdf-export]"
      ) as HTMLButtonElement;
      if (button) {
        button.disabled = false;
        button.innerHTML = "📊 Export Comprehensive Report";
      }
    }
  };

  return (
    <button
      data-pdf-export
      onClick={exportToPDF}
      style={{
        padding: "1rem 2rem",
        background: "rgb(201, 167, 80)",
        color: "white",
        border: "none",
        borderRadius: "12px",
        cursor: "pointer",
        fontSize: "1.1rem",
        fontWeight: "600",
        transition: "all 0.3s ease",
        boxShadow: "0 4px 15px rgba(201, 167, 80, 0.3)",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 6px 20px rgba(201, 167, 80, 0.4)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 4px 15px rgba(201, 167, 80, 0.3)";
      }}
    >
      📊 Export Comprehensive Report
    </button>
  );
};

export default PDFExport;
